// services/tenantService.js - Servicio de gestión de tenants (multi-tenancy)
import api from './api';
import { toast } from 'react-toastify';

const tenantService = {
  // Método auxiliar para manejar errores del API
  handleApiError(error, defaultMessage = 'Error en la operación') {
    if (!error.response) {
      // Error de conectividad
      toast.error('Error de conexión. Verifique su conexión a internet.', {
        theme: 'colored'
      });
      return 'Error de conexión';
    }

    const errorData = error.response.data;
    
    // Si hay múltiples errores de validación
    if (errorData.message && Array.isArray(errorData.message)) {
      const errorList = errorData.message.map((msg, index) => `${index + 1}. ${msg}`).join('\n');
      toast.error(
        <div>
          <strong>Errores encontrados:</strong>
          <br />
          <div style={{ whiteSpace: 'pre-line', marginTop: '8px' }}>
            {errorList}
          </div>
        </div>, 
        { 
          theme: 'colored',
          autoClose: 8000 // Más tiempo para leer múltiples errores
        }
      );
      return errorList;
    }

    // Error simple
    const message = errorData.message || defaultMessage;
    toast.error(message, { theme: 'colored' });
    return message;
  },

  // === GESTIÓN DE TENANTS ===

  // Obtener todos los tenants
  getAllTenants: async () => {
    try {
      const response = await api.get('/tenants');
      return {
        success: true,
        data: response.data || [],
        tenants: response.data || [] // Para compatibilidad
      };
    } catch (error) {
      console.error('Error obteniendo tenants:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error de conexión'
      };
    }
  },

  // Obtener solo tenants de tipo cliente (excluye admin tenant)
  getClientTenants: async () => {
    try {
      const response = await api.get('/tenants/clients');
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error obteniendo tenants cliente:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener tenants cliente'
      };
    }
  },

  // Obtener un tenant por ID
  getTenantById: async (id) => {
    try {
      const response = await api.get(`/tenants/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error obteniendo tenant:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error de conexión'
      };
    }
  },

  // Obtener estadísticas de un tenant
  getTenantStats: async (id) => {
    try {
      const response = await api.get(`/tenants/${id}/stats`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error obteniendo estadísticas del tenant:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener estadísticas del tenant'
      };
    }
  },

  // Crear nuevo tenant
  createTenant: async (tenantData) => {
    try {
      const response = await api.post('/tenants', tenantData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creando tenant:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al crear tenant'
      };
    }
  },

  // Actualizar tenant
  updateTenant: async (id, tenantData) => {
    try {
      const response = await api.put(`/tenants/${id}`, tenantData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error actualizando tenant:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al actualizar tenant'
      };
    }
  },

  // Cambiar estado de tenant (activar/desactivar)
  updateTenantStatus: async (id, isActive) => {
    try {
      const response = await api.patch(`/tenants/${id}/status`, { isActive });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error cambiando estado del tenant:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al cambiar estado'
      };
    }
  },

  // Eliminar tenant
  deleteTenant: async (id) => {
    try {
      await api.delete(`/tenants/${id}`);
      return {
        success: true
      };
    } catch (error) {
      console.error('Error eliminando tenant:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al eliminar tenant'
      };
    }
  },

  // === MÉTODOS AUXILIARES ===

  // Validar datos de tenant
  validateTenant(tenantData) {
    const errors = [];

    if (!tenantData.name || tenantData.name.trim() === '') {
      errors.push('El nombre del tenant es requerido');
    }

    if (!tenantData.code || tenantData.code.trim() === '') {
      errors.push('El código del tenant es requerido');
    }

    // Validar formato del código (solo letras, números y guiones bajos)
    if (tenantData.code && !/^[A-Z0-9_]+$/.test(tenantData.code)) {
      errors.push('El código debe contener solo letras mayúsculas, números y guiones bajos');
    }

    if (!tenantData.subdomain || tenantData.subdomain.trim() === '') {
      errors.push('El subdominio es requerido');
    }

    // Validar formato del subdominio (solo letras minúsculas, números y guiones)
    if (tenantData.subdomain && !/^[a-z0-9-]+$/.test(tenantData.subdomain)) {
      errors.push('El subdominio debe contener solo letras minúsculas, números y guiones');
    }

    if (!tenantData.plan || tenantData.plan.trim() === '') {
      errors.push('El plan es requerido');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Formatear datos para envío al backend
  formatTenantDataForApi(tenantData) {
    return {
      name: tenantData.name?.trim(),
      code: tenantData.code?.toUpperCase().trim(),
      subdomain: tenantData.subdomain?.toLowerCase().trim(),
      description: tenantData.description?.trim() || null,
      plan: tenantData.plan?.trim(),
      isActive: tenantData.isActive !== undefined ? tenantData.isActive : true,
      settings: tenantData.settings || {}
    };
  },

  // Calcular estadísticas de tenants
  calculateTenantsStats(tenants) {
    if (!Array.isArray(tenants) || tenants.length === 0) {
      return {
        totalTenants: 0,
        activeTenants: 0,
        inactiveTenants: 0,
        planDistribution: {},
        activationPercentage: 0
      };
    }

    const totalTenants = tenants.length;
    const activeTenants = tenants.filter(tenant => tenant.isActive).length;
    const inactiveTenants = totalTenants - activeTenants;
    const activationPercentage = totalTenants > 0 ? (activeTenants / totalTenants) * 100 : 0;

    // Distribución por plan
    const planDistribution = tenants.reduce((acc, tenant) => {
      const plan = tenant.plan || 'Sin Plan';
      acc[plan] = (acc[plan] || 0) + 1;
      return acc;
    }, {});

    return {
      totalTenants,
      activeTenants,
      inactiveTenants,
      planDistribution,
      activationPercentage: Math.round(activationPercentage * 100) / 100
    };
  },

  // Obtener opciones de planes disponibles
  getAvailablePlans() {
    return [
      { value: 'BASIC', label: 'Básico' },
      { value: 'PROFESSIONAL', label: 'Profesional' },
      { value: 'ENTERPRISE', label: 'Empresarial' },
      { value: 'CUSTOM', label: 'Personalizado' }
    ];
  },

  // Validar permisos de usuario para operaciones de tenant
  canManageTenants(user) {
    // Solo super admins pueden gestionar tenants
    return user && user.roles && user.roles.some(role => 
      role.name === 'super_admin' || role.name === 'admin'
    );
  },

  // Generar código de tenant basado en el nombre
  generateTenantCode(name) {
    if (!name) return '';
    
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '') // Remover caracteres especiales
      .replace(/\s+/g, '_') // Reemplazar espacios con guiones bajos
      .substring(0, 20); // Limitar a 20 caracteres
  },

  // Generar subdominio basado en el nombre
  generateSubdomain(name) {
    if (!name) return '';
    
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remover caracteres especiales
      .replace(/\s+/g, '-') // Reemplazar espacios con guiones
      .substring(0, 30); // Limitar a 30 caracteres
  }
};

export default tenantService; 